import React, { Fragment } from "react";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { neutralBulkValidationSchema } from "../../../../helpers/validationSchema";
import { Formik } from "formik";
import FormField from "../../../common/FormField/FormField";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../../common/Buttons";
import { Modal } from "@material-ui/core";
import {
  Overlay,
  ModalCard,
  ModalHeader,
  ModalBody,
  ModalTitle,
  ModeBtn,
  ModeToggle,
  StyledForm,
  FooterActions,
  FormSection,
  HeaderAccent,
  HeaderContent,
  IconBadge,
  CloseBtn,
  SectionLabel,
} from "../../../common/MutipleHearings";
import { Label } from "../../ManagementTools/component/SplitAndUpload";
import { CustomFileUpload } from "../../../common/FormInputs";
import { getFileName, getErrorMessage } from "../../../../helpers/functions";
import CaseService from "../../../../services/CaseService";
import AWSService from "../../../../services/AWSService";

const inputModes = { MANUAL: "manual", EXCEL: "excel" };

const validFormat = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

const NeutralModal = ({ open, onClose, MTRef }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true });

      let payload = {};
      if (values.mode === inputModes.MANUAL) {
        payload = {
          name: values.name,
          email: values.email,
          mobile: values.mobile,
        };
      } else if (values.mode === inputModes.EXCEL) {
        payload = {
          fileUrl: values.fileUrl,
        };
      }
      const response = await CaseService.createNeutral(payload);
      if (response) {
        enqueueSnackbar(response.message, {
          variant: "success",
        });
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
      MTRef.current.onQueryChange();
      onClose();
    }
  }

  async function s3Upload(file, field, setFieldValue, setFieldError) {
    if (!file) return;
    try {
      const errorMessage = "Invalid File Type";
      if (!validFormat.includes(file?.type)) throw errorMessage;
      setLoader({ state: true, message: `Uploading file: ${file.name} …` });
      const s3Response = await AWSService.getS3URL({ key: file.name });
      if (s3Response.url) {
        const res = await AWSService.uploadToS3(s3Response.url, file);
        setFieldValue(field, res);
        enqueueSnackbar("Successfully Uploaded", { variant: "success" });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      setFieldError(field, "Could not upload this file");
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <Fragment>
      <Modal open={open} onClose={() => onClose()}>
        <Overlay>
          <ModalCard>
            <ModalHeader>
              <HeaderAccent />
              <HeaderContent>
                <IconBadge>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </IconBadge>
                <ModalTitle>Add Neutal</ModalTitle>
              </HeaderContent>
              <CloseBtn onClick={() => onClose()} aria-label="Close">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ModalHeader>

            {/* ── Formik Body ── */}
            <ModalBody>
              <Formik
                initialValues={{
                  name: "",
                  email: "",
                  mobile: "",
                  fileUrl: "",
                  mode: "manual",
                }}
                onSubmit={onFormSubmit}
                validationSchema={neutralBulkValidationSchema}
                validateOnBlur
                enableReinitialize
                validateOnChange
              >
                {({
                  values,
                  handleChange,
                  errors,
                  handleSubmit,
                  touched,
                  handleBlur,
                  setFieldValue,
                  setFieldError,
                  isValid,
                }) => (
                  <StyledForm onSubmit={handleSubmit}>
                    {/* Input Mode Toggle */}
                    <FormSection>
                      <SectionLabel>Input Method</SectionLabel>
                      <ModeToggle>
                        <ModeBtn
                          type="button"
                          active={values.mode === inputModes.MANUAL}
                          onClick={() => {
                            setFieldValue("mode", inputModes.MANUAL);
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Individual
                        </ModeBtn>
                        <ModeBtn
                          type="button"
                          active={values.mode === inputModes.EXCEL}
                          onClick={() =>
                            setFieldValue("mode", inputModes.EXCEL)
                          }
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          Bulk
                        </ModeBtn>
                      </ModeToggle>
                    </FormSection>

                    {/* Manual / Excel Inputs */}
                    <FormSection>
                      {values.mode === inputModes.MANUAL ? (
                        [
                          {
                            name: "name",
                            type: "text",
                            label: "Name",
                            required: true,
                          },
                          {
                            name: "email",
                            type: "email",
                            label: "Email",
                            required: true,
                          },
                          {
                            name: "mobile",
                            type: "mobile",
                            label: "Phone",
                            required: true,
                          },
                        ].map((input, key) => (
                          <FormField
                            key={key}
                            white
                            {...{
                              input,
                              values,
                              errors,
                              touched,
                              handleBlur,
                              handleChange,
                              setFieldValue,
                            }}
                          />
                        ))
                      ) : (
                        <>
                          <Label>
                            Upload File
                            <span className="required-star"> *</span>
                          </Label>
                          <CustomFileUpload
                            error={touched["fileUrl"] && errors["fileUrl"]}
                            setFile={(file) =>
                              s3Upload(
                                file,
                                "fileUrl",
                                setFieldValue,
                                setFieldError,
                              )
                            }
                            width={"100%"}
                            accept={".xlsx"}
                            file={{ name: getFileName(values["fileUrl"]) }}
                          />
                        </>
                      )}
                    </FormSection>

                    <FooterActions>
                      <PrimaryOutlinedCloseCTAButton
                        type="button"
                        onClick={onClose}
                        style={{ flex: 1 }}
                      >
                        Cancel
                      </PrimaryOutlinedCloseCTAButton>
                      <PrimaryCTAButton
                        type="submit"
                        onClick={handleSubmit}
                        style={{ flex: 1 }}
                      >
                        Submit
                      </PrimaryCTAButton>
                    </FooterActions>
                  </StyledForm>
                )}
              </Formik>
            </ModalBody>
          </ModalCard>
        </Overlay>
      </Modal>
    </Fragment>
  );
};

export default NeutralModal;